<script setup>
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue'
import { usePaginacao } from '@/composables/usePaginacao'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { filialSearch } from '@/search-definitions/filialSearch'
import { useConversaoQuantidade } from '@/composables/useConversaoQuantidade'

const authStore = useAuthStore()

const props = defineProps({
  idEmbarque: { type: Number, default: null },
  idPurchaseOrder: { type: Number, default: null },
  itensEmbarque: { type: Array, default: () => [] },
  packingListItems: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  configBuscaParceiroPCat2: { type: Object, default: null }
})

const emit = defineEmits([
  'show-notification',
  'itens-atualizados'
])

// ============================================================
// ESTADO
// ============================================================
const produtosDisponiveis = ref([])
const isLoadingDisponiveis = ref(false)
const mostrarPainelDisponiveis = ref(false)
const draggingOverId = ref(null)
const editingItemId = ref(null)

// ============================================================
// PAGINAÇÃO DOS PRODUTOS DISPONÍVEIS
// ============================================================
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

// ============================================================
// FILTROS DO PAINEL DE PRODUTOS DISPONÍVEIS
// ============================================================
const filtros = ref({
  termoFornecedor: [],
  numeroContratoFornecedor: [],
  numeroCotaReferencia: [],
  numeroDocumentoEntrada: [],
  termoProduto: [],
  idFilial: null
})

const inputsFiltro = ref({
  termoFornecedor: '',
  numeroContratoFornecedor: '',
  numeroCotaReferencia: '',
  numeroDocumentoEntrada: '',
  termoProduto: ''
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const adicionarTag = (campo, event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  const valor = inputsFiltro.value[campo].trim()
  if (valor && !filtros.value[campo].includes(valor)) {
    filtros.value[campo].push(valor)
  }
  inputsFiltro.value[campo] = ''
}

const removerTag = (campo, index) => {
  filtros.value[campo].splice(index, 1)
}

const handleBackspace = (campo, event) => {
  // Se o input estiver vazio e houver tags, remove a última tag
  if (inputsFiltro.value[campo] === '' && filtros.value[campo].length > 0) {
    event.preventDefault()
    filtros.value[campo].pop()
  }
}

const mostrarFiltrosColapsados = ref(false)

// ============================================================
// CARREGAR PRODUTOS DISPONÍVEIS
// ============================================================
let scrollPositionDisponiveis = 0

const carregarProdutosDisponiveis = async (pageNumber = null) => {
  if (!props.idEmbarque) return

  // Salvar scroll position antes de iniciar loading
  const disponiveisBody = document.querySelector('.disponiveis-body')
  if (disponiveisBody) {
    scrollPositionDisponiveis = disponiveisBody.scrollTop
  }

  isLoadingDisponiveis.value = true
  try {
    const params = injetarParametros(pageNumber || paginacao.value.CurrentPage)
    if (props.idPurchaseOrder) {
      params.append('idPurchaseOrder', props.idPurchaseOrder)
    }
    // Adicionar filtros se tiverem itens - converter Proxy para array normal
    Object.keys(filtros.value).forEach(key => {
      if (key === 'idFilial') return // tratado separadamente abaixo
      if (filtros.value[key]?.length > 0) {
        // Converte Proxy para array normal e garante que são strings
        Array.from(filtros.value[key]).map(v => String(v)).forEach(v => {
          params.append(key, v)
        })
      }
    })

    // Filtro de filial
    if (filtros.value.idFilial) {
      params.append('idFilial', filtros.value.idFilial)
    }

    const url = `/DocumentosEntradaItens/disponiveis-embarque?${params.toString()}`

    const res = await api.get(url)
    produtosDisponiveis.value = res.data || []
    atualizarPaginacao(res.headers)
  } catch (e) {
    console.error('Erro ao carregar produtos disponíveis:', e)
    emit('show-notification', { message: 'Erro ao carregar produtos disponíveis.', type: 'error' })
    produtosDisponiveis.value = []
  } finally {
    isLoadingDisponiveis.value = false
    // Restaurar scroll position após atualização
    await nextTick()
    const disponiveisBody = document.querySelector('.disponiveis-body')
    if (disponiveisBody) {
      disponiveisBody.scrollTop = scrollPositionDisponiveis
    }
  }
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    carregarProdutosDisponiveis(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  carregarProdutosDisponiveis(1)
}

// Debounce para filtros: aguarda 400ms após a última digitação
let debounceTimer = null
watch(filtros, () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    if (mostrarPainelDisponiveis.value || produtosDisponiveis.value.length > 0) {
      carregarProdutosDisponiveis(1)
    }
  }, 400)
}, { deep: true })

// Abre o painel automaticamente se não houver itens alocados
watch(() => props.itensEmbarque, (novaLista) => {
  if (novaLista.length === 0 && !mostrarPainelDisponiveis.value && props.idEmbarque) {
    mostrarPainelDisponiveis.value = true
  }
}, { immediate: true })

watch(() => props.idEmbarque, (novoId) => {
  if (novoId) {
    carregarProdutosDisponiveis(1)
  }
}, { immediate: true })

const togglePainelDisponiveis = () => {
  mostrarPainelDisponiveis.value = !mostrarPainelDisponiveis.value
  if (mostrarPainelDisponiveis.value && produtosDisponiveis.value.length === 0) {
    carregarProdutosDisponiveis(1)
  }
}

const temSaldoPendente = computed(() => {
  return produtosDisponiveis.value.some(p => (Number(p.saldoDisponivelForn) || 0) > 0)
})

const produtosDisponiveisOrdenados = computed(() => {
  return [...produtosDisponiveis.value].sort((a, b) => {
    const saldoA = (Number(a.saldoDisponivelForn) || 0) > 0 ? 0 : 1
    const saldoB = (Number(b.saldoDisponivelForn) || 0) > 0 ? 0 : 1
    return saldoA - saldoB
  })
})

// ============================================================
// ITENS ALOCADOS (enriquecidos com dados dos disponíveis)
// ============================================================
const itensAlocadosEnriquecidos = computed(() => {
  return props.itensEmbarque.map(item => {
    const disp = produtosDisponiveis.value.find(
      p => p.idDocumentoEntradaItem === item.idDocumentoEntradaItem
    )
    return {
      ...item,
      nomeProduto: item.nomeProduto || item.nomeProduto || (disp ? disp.nomeProduto : '') || 'Produto #' + item.idProduto,
      sku: item.sku || item.skuProduto || (disp ? disp.sku : '') || '',
      idFilial: item.idFilial || (disp ? disp.idFilial : null) || null,
      nomeFilial: item.nomeFilial || (disp ? disp.nomeFilial : '') || '',
      nomeFornecedor: item.nomeFornecedor || (disp ? disp.nomeFornecedor : '') || '',
      numeroDocumentoEntrada: item.numeroDocumentoEntrada || (disp ? disp.numeroDocumentoEntrada : '') || '',
      numeroContratoFornecedor: item.numeroContratoFornecedor || (disp ? disp.numeroContratoFornecedor : '') || '',
      nomeReferenciaCota: item.nomeReferenciaCota || (disp ? disp.nomeReferenciaCota : '') || '',
      numeroLoteFornecedor: item.numeroLoteFornecedor || (disp ? disp.numeroLoteFornecedor : '') || '',
      lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || (disp ? disp.lstUnidadeMedidaFornecedor : '') || '',
      lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || (disp ? disp.lstUnidadeMedidaBasica : '') || '',
      precoUnitario: Number(item.precoUnitario) || (disp ? Number(disp.precoUnitario) : 0) || 0,
      codigoNcm: item.codigoNcm || (disp ? disp.codigoNcm : '') || '',
      fatorConversao: Number(item.fatorConversao) || (disp ? Number(disp.fatorConversao) : 0) || 0
    }
  })
})

// ============================================================
// VERIFICAÇÃO DE ITENS ASSOCIADOS AO PACKING LIST
// ============================================================
const itensAssociadosAPackingList = computed(() => {
  const map = {}
  for (const pl of (props.packingListItems || [])) {
    for (const prod of (pl.produtos || [])) {
      if (prod.idEmbarqueItem) {
        map[prod.idEmbarqueItem] = true
      }
    }
  }
  return map
})

const itemEstaAssociadoAPackingList = (idEmbarqueItem) => {
  return !!itensAssociadosAPackingList.value[idEmbarqueItem]
}

// ============================================================
// DRAG & DROP
// ============================================================
const onDragStart = (event, item) => {
  if (!props.podeEditar) {
    event.preventDefault()
    return
  }
  const data = {
    idDocumentoEntradaItem: item.idDocumentoEntradaItem,
    idProduto: item.idProduto,
    nomeProduto: item.nomeProduto || '',
    sku: item.sku || '',
    idFilial: item.idFilial,
    nomeFilial: item.nomeFilial || '',
    nomeFornecedor: item.nomeFornecedor || '',
    numeroDocumentoEntrada: item.numeroDocumentoEntrada || '',
    numeroContratoFornecedor: item.numeroContratoFornecedor || '',
    nomeReferenciaCota: item.nomeReferenciaCota || '',
    lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || '',
    fatorConversao: item.fatorConversao || 0,
    precoUnitario: Number(item.precoUnitario) || 0,
    saldoDisponivelForn: Number(item.saldoDisponivelForn) || 0,
    saldoDisponivelBase: Number(item.saldoDisponivelBase) || 0,
    codigoNcm: item.codigoNcm || '',
    idLoteFabricacao: item.idLoteFabricacao || null,
    numeroLoteFornecedor: item.numeroLoteFornecedor || '',
    pesoBrutoDocumentalKg: Number(item.pesoBrutoDocumentalKg) || 0,
    pesoLiquidoDocumentalKg: Number(item.pesoLiquidoDocumentalKg) || 0
  }
  event.dataTransfer.setData('application/json', JSON.stringify(data))
  event.dataTransfer.effectAllowed = 'move'
}

const onDragEnter = () => {
  if (!props.podeEditar) return
  draggingOverId.value = '__alocados__'
}

const onDragOver = (event, id) => {
  if (!props.podeEditar) return
  event.preventDefault()
  draggingOverId.value = id
  event.dataTransfer.dropEffect = 'move'
}

const onDragLeave = (event) => {
  if (draggingOverId.value === '__alocados__') {
    const related = event.relatedTarget
    if (!related || !event.currentTarget.contains(related)) {
      draggingOverId.value = null
    }
  }
}

// ============================================================
// MODAL DE ALOCAÇÃO (APENAS QUANTIDADES)
// ============================================================
const showFractionModal = ref(false)

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

watch(showFractionModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onBeforeUnmount(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

const fractionModalData = ref({
  idDocumentoEntradaItem: null,
  idProduto: null,
  nomeProduto: '',
  sku: '',
  nomeFornecedor: '',
  numeroDocumentoEntrada: '',
  numeroContratoFornecedor: '',
  nomeReferenciaCota: '',
  lstUnidadeMedidaFornecedor: '',
  lstUnidadeMedidaBasica: '',
  fatorConversao: 0,
  precoUnitario: 0,
  saldoDisponivelForn: 0,
  saldoDisponivelBase: 0,
  quantidadeForn: 0,
  quantidadeBase: 0,
  codigoNcm: '',
  idLoteFabricacao: null,
  numeroLoteFornecedor: '',
  idFilial: null,
  nomeFilial: '',
  pesoBrutoDocumentalKg: null,
  pesoLiquidoDocumentalKg: null
})

const onDrop = (event) => {
  draggingOverId.value = null
  if (!props.podeEditar) return

  editingItemId.value = null

  const rawData = event.dataTransfer.getData('application/json')
  if (!rawData) return

  try {
    const data = JSON.parse(rawData)

    fractionModalData.value = {
      idDocumentoEntradaItem: data.idDocumentoEntradaItem,
      idProduto: data.idProduto,
      nomeProduto: data.nomeProduto,
      sku: data.sku,
      nomeFornecedor: data.nomeFornecedor || '',
      numeroDocumentoEntrada: data.numeroDocumentoEntrada || '',
      numeroContratoFornecedor: data.numeroContratoFornecedor || '',
      nomeReferenciaCota: data.nomeReferenciaCota || '',
      lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
      lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
      fatorConversao: Number(data.fatorConversao) || 0,
      precoUnitario: Number(data.precoUnitario) || 0,
      saldoDisponivelForn: Number(data.saldoDisponivelForn) || 0,
      saldoDisponivelBase: Number(data.saldoDisponivelBase) || 0,
      quantidadeForn: Number(data.saldoDisponivelForn) || 0,
      quantidadeBase: Number(data.saldoDisponivelBase) || 0,
      codigoNcm: data.codigoNcm || '',
      idLoteFabricacao: data.idLoteFabricacao || null,
      numeroLoteFornecedor: data.numeroLoteFornecedor || '',
      idFilial: data.idFilial || null,
      nomeFilial: data.nomeFilial || '',
      pesoBrutoDocumentalKg: Number(data.pesoBrutoDocumentalKg) || null,
      pesoLiquidoDocumentalKg: Number(data.pesoLiquidoDocumentalKg) || null
    }

    showFractionModal.value = true
  } catch (e) {
    console.error('Erro ao processar drop:', e)
  }
}

const { simular } = useConversaoQuantidade()

// Conversão centralizada (backend = fonte da verdade).
// Chamada no lost focus do campo de quantidade ou no botão "Calcular".
const calcularConversao = async (campo) => {
  const data = fractionModalData.value
  if (!data || !data.idProduto) return

  const saldoForn = Number(data.saldoDisponivelForn) || 0
  const saldoBase = Number(data.saldoDisponivelBase) || 0
  if (Number(data.quantidadeForn) > saldoForn) data.quantidadeForn = saldoForn
  if (Number(data.quantidadeBase) > saldoBase) data.quantidadeBase = saldoBase

  try {
    const resultado = await simular({
      idProduto: data.idProduto,
      quantidadeFornecedor: Number(data.quantidadeForn) || 0,
      quantidadeBase: Number(data.quantidadeBase) || 0,
      fatorConversao: Number(data.fatorConversao) || null,
      campoOrigem: campo === 'base' ? 'Base' : 'Fornecedor'
    })
    data.quantidadeForn = resultado.quantidadeFornecedor
    data.quantidadeBase = resultado.quantidadeBase
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao calcular a conversão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const ajustarQuantidade = (campo, delta) => {
  const data = fractionModalData.value
  const saldo = campo === 'quantidadeForn' ? data.saldoDisponivelForn : data.saldoDisponivelBase
  const atual = Number(data[campo]) || 0
  const novo = Math.max(0, Math.min(saldo, atual + delta))
  data[campo] = parseFloat(novo.toFixed(4))
}

const confirmarAlocacao = () => {
  const data = fractionModalData.value
  if (data.quantidadeForn <= 0 && data.quantidadeBase <= 0) {
    emit('show-notification', { message: 'Informe pelo menos uma quantidade maior que zero.', type: 'warning' })
    return
  }

  prosseguirAlocacao()
}

const prosseguirAlocacao = async () => {
  const data = fractionModalData.value

  const payload = {
    idEmbarque: props.idEmbarque,
    idDocumentoEntradaItem: data.idDocumentoEntradaItem,
    idProduto: data.idProduto,
    quantidadeEmbarcadaUnFornecedor: data.quantidadeForn,
    quantidadeEmbarcadaUnBasica: data.quantidadeBase,
    fatorConversao: data.fatorConversao,
    lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
    idLoteFabricacao: data.idLoteFabricacao || null,
    pesoLiquidoDocumentalKg: data.pesoLiquidoDocumentalKg || 0,
    pesoBrutoDocumentalKg: data.pesoBrutoDocumentalKg || 0
  }

  try {
    if (editingItemId.value) {
      await api.put(`/EmbarqueItems/${editingItemId.value}`, payload)
      emit('show-notification', { message: 'Item atualizado com sucesso!', type: 'success' })
    } else {
      await api.post('/EmbarqueItems', payload)
      emit('show-notification', { message: 'Item alocado com sucesso!', type: 'success' })
    }

    showFractionModal.value = false
    editingItemId.value = null
    emit('itens-atualizados')
    await carregarProdutosDisponiveis()
  } catch (e) {
    console.error('Erro ao salvar item do embarque:', e)
    const msg = e.response?.data?.message || 'Erro ao alocar produto no embarque.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const fecharModal = () => {
  showFractionModal.value = false
  editingItemId.value = null
}

const handleModalKeydown = (e) => {
  if (e.key === 'Enter' && showFractionModal.value) {
    if (!modalAlocacaoRef.value?.contains(e.target)) return
    e.preventDefault()
    confirmarAlocacao()
  }
}

watch(() => showFractionModal.value, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleModalKeydown)
  } else {
    document.removeEventListener('keydown', handleModalKeydown)
  }
})

// ============================================================
// EDIÇÃO E EXCLUSÃO DE ITENS ALOCADOS
// ============================================================
const abrirEdicao = async (itemEmb) => {
  if (!props.podeEditar) return

  editingItemId.value = itemEmb.idEmbarqueItem

  // Calcular saldo disponível considerando a quantidade atual do item
  const qtdFornAtual = Number(itemEmb.quantidadeEmbarcadaUnFornecedor) || 0
  const qtdBaseAtual = Number(itemEmb.quantidadeEmbarcadaUnBasica) || 0

  const disp = produtosDisponiveis.value.find(
    p => p.idDocumentoEntradaItem === itemEmb.idDocumentoEntradaItem
  )
  const saldoPendenteForn = disp ? (Number(disp.saldoDisponivelForn) || 0) : 0
  const saldoPendenteBase = disp ? (Number(disp.saldoDisponivelBase) || 0) : 0

  const saldoDisponivelForn = qtdFornAtual + saldoPendenteForn
  const saldoDisponivelBase = qtdBaseAtual + saldoPendenteBase

  fractionModalData.value = {
    idDocumentoEntradaItem: itemEmb.idDocumentoEntradaItem,
    idProduto: itemEmb.idProduto,
    nomeProduto: itemEmb.nomeProduto || '',
    sku: itemEmb.sku || '',
    nomeFornecedor: itemEmb.nomeFornecedor || (disp ? disp.nomeFornecedor : '') || '',
    numeroDocumentoEntrada: itemEmb.numeroDocumentoEntrada || (disp ? disp.numeroDocumentoEntrada : '') || '',
    numeroContratoFornecedor: itemEmb.numeroContratoFornecedor || (disp ? disp.numeroContratoFornecedor : '') || '',
    nomeReferenciaCota: itemEmb.nomeReferenciaCota || (disp ? disp.nomeReferenciaCota : '') || '',
    lstUnidadeMedidaFornecedor: itemEmb.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: itemEmb.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(itemEmb.fatorConversao) || 0,
    precoUnitario: 0,
    saldoDisponivelForn,
    saldoDisponivelBase,
    quantidadeForn: qtdFornAtual,
    quantidadeBase: qtdBaseAtual,
    codigoNcm: itemEmb.codigoNcm || '',
    idLoteFabricacao: itemEmb.idLoteFabricacao || null,
    numeroLoteFornecedor: itemEmb.numeroLoteFornecedor || (disp ? disp.numeroLoteFornecedor : '') || '',
    idFilial: itemEmb.idFilial || (disp ? disp.idFilial : null) || null,
    nomeFilial: itemEmb.nomeFilial || (disp ? disp.nomeFilial : '') || '',
    pesoBrutoDocumentalKg: Number(itemEmb.pesoBrutoDocumentalKg) || null,
    pesoLiquidoDocumentalKg: Number(itemEmb.pesoLiquidoDocumentalKg) || null
  }

  showFractionModal.value = true
}

const excluirItem = async (id) => {
  try {
    await api.delete(`/EmbarqueItems/${id}`)
    emit('show-notification', { message: 'Item removido do embarque!', type: 'success' })
    emit('itens-atualizados')
    await carregarProdutosDisponiveis()
  } catch (e) {
    console.error('Erro ao excluir item:', e)
    const data = e.response?.data
    let msg = ''
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (e.response?.status === 400) {
      msg = 'Não foi possível remover o item. Verifique se ele não está vinculado a outros registros.'
    } else if (e.response?.status === 404) {
      msg = 'Item não encontrado. Pode ter sido removido por outro usuário.'
    } else if (e.response?.status === 409) {
      msg = 'Conflito de concorrência: outro usuário alterou este registro. Recarregue e tente novamente.'
    } else {
      msg = 'Erro ao remover item do embarque. Tente novamente.'
    }
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// HELPERS
// ============================================================
const getTextoUnidade = (id) => {
  const lista = props.listas.LstUnidadeMedidaFornecedor || props.listas.LstUnidadeMedidaBase || props.listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '0'
  const num = Number(valor)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  })
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

</script>

<template>
  <div class="produtos-smart-mode">
    <!-- ============================================================ -->
    <!-- PAINEL DE PRODUTOS DISPONÍVEIS (colapsável, à esquerda) -->
    <!-- ============================================================ -->
    <div class="disponiveis-panel" :class="{ 'disponiveis-visible': mostrarPainelDisponiveis }">
      <div class="disponiveis-panel-inner">
        <div class="disponiveis-header">
          <div class="disponiveis-header-top">
            <h5 class="mb-0 d-flex align-items-center gap-2">
              <i class="bi bi-box-seam"></i>
              Produtos Disponíveis
              <i class="bi bi-info-circle text-muted" title="Produtos de documentos de entrada aprovados"
                style="font-size: 0.75rem; cursor: help;"></i>
              <span v-if="paginacao.TotalCount > 0"
                class="badge bg-secondary bg-opacity-10 text-body-emphasis border border-secondary-subtle ms-1">
                {{ paginacao.TotalCount }}
              </span>
            </h5>
            <button type="button" class="btn btn-sm btn-outline-warning border-0 p-1" title="Fechar painel"
              @click="togglePainelDisponiveis">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <small class="text-muted">Arraste cards para alocar no embarque</small>

          <!-- ============================================================ -->
          <!-- FILTROS DO PAINEL DE PRODUTOS DISPONÍVEIS -->
          <!-- ============================================================ -->
          <div class="filtros-container mt-2">
            <button type="button"
              class="btn btn-sm btn-link text-body-secondary p-0 mb-1 text-decoration-none d-flex align-items-center gap-1"
              @click="mostrarFiltrosColapsados = !mostrarFiltrosColapsados">
              <i class="bi" :class="mostrarFiltrosColapsados ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              {{ mostrarFiltrosColapsados ? 'Ocultar Filtros' : 'Mostrar Filtros' }}
            </button>

            <div v-show="mostrarFiltrosColapsados" class="filtros-disponiveis border-top pt-2">
              <div class="alert alert-info py-1 px-2 mb-2" style="font-size: 0.65rem;">
                <i class="bi bi-info-circle me-1"></i>
                Pressione <b>Enter</b> para adicionar múltiplos valores. A busca é parcial automática.
              </div>

              <!-- Filial -->
              <div class="filtro-group">
                <label class="filtro-label">Filial</label>
                <CampoBusca
                  id="filtroIdFilial"
                  v-model="filtros.idFilial"
                  :configuracao-busca="configBuscaFilial"
                  placeholder="Selecionar filial..."
                  class="campo-busca-sm"
                />
              </div>

              <!-- Fornecedor -->
              <div class="filtro-group">
                <label class="filtro-label">
                  Fornecedor
                </label>
                <div class="tags-input-container">
                  <div v-for="(tag, idx) in filtros.termoFornecedor" :key="`forn-${idx}`" class="tag-badge">
                    {{ tag }}
                    <i class="bi bi-x-circle ms-1" @click="removerTag('termoFornecedor', idx)"></i>
                  </div>
                  <input type="text" class="form-control form-control-sm" v-model="inputsFiltro.termoFornecedor"
                    placeholder="Doc, Fantasia..." @keydown.enter.prevent="adicionarTag('termoFornecedor', $event)"
                    @keydown.delete="handleBackspace('termoFornecedor', $event)">
                </div>
              </div>

              <!-- Nº Contrato (Fornecedor) -->
              <div class="filtro-group">
                <label class="filtro-label">Nº Contrato</label>
                <div class="tags-input-container">
                  <div v-for="(tag, idx) in filtros.numeroContratoFornecedor" :key="`contrato-${idx}`"
                    class="tag-badge">
                    {{ tag }}
                    <i class="bi bi-x-circle ms-1" @click="removerTag('numeroContratoFornecedor', idx)"></i>
                  </div>
                  <input type="text" class="form-control form-control-sm"
                    v-model="inputsFiltro.numeroContratoFornecedor" placeholder="Contrato..."
                    @keydown.enter.prevent="adicionarTag('numeroContratoFornecedor', $event)"
                    @keydown.delete="handleBackspace('numeroContratoFornecedor', $event)">
                </div>
              </div>

              <!-- Nº Cota / Referência -->
              <div class="filtro-group">
                <label class="filtro-label">Nº Cota / Referência</label>
                <div class="tags-input-container">
                  <div v-for="(tag, idx) in filtros.numeroCotaReferencia" :key="`cota-${idx}`" class="tag-badge">
                    {{ tag }}
                    <i class="bi bi-x-circle ms-1" @click="removerTag('numeroCotaReferencia', idx)"></i>
                  </div>
                  <input type="text" class="form-control form-control-sm" v-model="inputsFiltro.numeroCotaReferencia"
                    placeholder="Referência..." @keydown.enter.prevent="adicionarTag('numeroCotaReferencia', $event)"
                    @keydown.delete="handleBackspace('numeroCotaReferencia', $event)">
                </div>
              </div>

              <!-- Documento de Entrada -->
              <div class="filtro-group">
                <label class="filtro-label">Doc. Entrada</label>
                <div class="tags-input-container">
                  <div v-for="(tag, idx) in filtros.numeroDocumentoEntrada" :key="`doc-${idx}`" class="tag-badge">
                    {{ tag }}
                    <i class="bi bi-x-circle ms-1" @click="removerTag('numeroDocumentoEntrada', idx)"></i>
                  </div>
                  <input type="text" class="form-control form-control-sm" v-model="inputsFiltro.numeroDocumentoEntrada"
                    placeholder="Número..." @keydown.enter.prevent="adicionarTag('numeroDocumentoEntrada', $event)"
                    @keydown.delete="handleBackspace('numeroDocumentoEntrada', $event)">
                </div>
              </div>

              <!-- Produto -->
              <div class="filtro-group">
                <label class="filtro-label">Produto</label>
                <div class="tags-input-container">
                  <div v-for="(tag, idx) in filtros.termoProduto" :key="`prod-${idx}`" class="tag-badge">
                    {{ tag }}
                    <i class="bi bi-x-circle ms-1" @click="removerTag('termoProduto', idx)"></i>
                  </div>
                  <input type="text" class="form-control form-control-sm" v-model="inputsFiltro.termoProduto"
                    placeholder="Nome ou SKU..." @keydown.enter.prevent="adicionarTag('termoProduto', $event)"
                    @keydown.delete="handleBackspace('termoProduto', $event)">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="disponiveis-body">
          <div v-if="isLoadingDisponiveis" class="local-loading-overlay"></div>
          <div v-if="isLoadingDisponiveis" class="local-loading-spinner">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
          </div>

          <template v-if="produtosDisponiveis.length === 0 && !isLoadingDisponiveis">
            <div class="empty-state">
              <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
              <p class="mb-0">Nenhum produto disponível.</p>
              <small class="text-muted">Verifique se existem documentos de entrada aprovados com movimentação de estoque
                vinculados a uma cota.</small>
            </div>
          </template>
          <template v-else>
            <div v-for="item in produtosDisponiveisOrdenados" :key="item.idDocumentoEntradaItem" draggable="true"
              @dragstart="onDragStart($event, item)" class="draggable-card card-produto-disponivel"
              :class="{ 'card-saldo-esgotado': (Number(item.saldoDisponivelForn) || 0) <= 0 }">
              <div class="card-sku" v-if="item.sku">{{ item.sku }}</div>
              <div class="card-title">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</div>
              <div class="card-filial" v-if="item.nomeFilial">
                <i class="bi bi-geo-alt me-1"></i>
                <span>{{ 'Filial ' + item.idFilial + ' - ' + item.nomeFilial }}</span>
              </div>
              <div class="card-info-grid">
                <div class="card-info-row">
                  <div class="card-info-item" v-if="item.nomeFornecedor">
                    <span class="info-label-tag">Fornecedor</span>
                    <span class="info-value-tag">{{ item.nomeFornecedor }}</span>
                  </div>
                  <div class="card-info-item" v-if="item.numeroDocumentoEntrada">
                    <span class="info-label-tag">Doc. Entrada</span>
                    <span class="info-value-tag">{{ item.numeroDocumentoEntrada }}</span>
                  </div>
                </div>
                <div class="card-info-row">
                  <div class="card-info-item" v-if="item.numeroContratoFornecedor">
                    <span class="info-label-tag">Contrato</span>
                    <span class="info-value-tag">{{ item.numeroContratoFornecedor }}</span>
                  </div>
                  <div class="card-info-item" v-if="item.nomeReferenciaCota">
                    <span class="info-label-tag">Cota</span>
                    <span class="info-value-tag">{{ item.nomeReferenciaCota }}</span>
                  </div>
                  <div class="card-info-item" v-if="item.numeroLoteFornecedor">
                    <span class="info-label-tag">Lote Fab.</span>
                    <span class="info-value-tag">{{ item.numeroLoteFornecedor }}</span>
                  </div>
                </div>
              </div>
              <div class="card-saldo-separator"></div>
              <div class="card-saldo">
                <div class="saldo-row">
                  <span class="saldo-label">Qtd (Forn):</span>
                  <span class="saldo-value">{{ formatarQtd(item.saldoDisponivelForn) }} {{
                    getTextoUnidade(item.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="saldo-row">
                  <span class="saldo-label">Qtd (Base):</span>
                  <span class="saldo-value">{{ formatarQtd(item.saldoDisponivelBase) }} {{
                    getTextoUnidade(item.lstUnidadeMedidaBasica) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- PAGINAÇÃO DOS PRODUTOS DISPONÍVEIS (fixa no rodapé do painel) -->
        <div v-if="paginacao.TotalCount > 0" class="paginacao-disponiveis">
          <div class="d-flex flex-wrap align-items-center justify-content-between w-100 gap-2">
            <div class="d-flex flex-wrap align-items-center gap-3">
              <span class="text-muted small">
                Página <strong>{{ paginacao.CurrentPage || 0 }}</strong> de <strong>{{ paginacao.TotalPages || 0 }}</strong>
                <span class="d-none d-sm-inline">(Total: {{ paginacao.TotalCount || 0 }} registos)</span>
              </span>

              <div class="d-flex align-items-center gap-2">
                <label class="text-muted small mb-0 d-none d-sm-inline">Itens/página:</label>
                <select
                  class="form-select form-select-sm w-auto cursor-pointer"
                  :value="paginacao.PageSize"
                  @change="mudarTamanhoPagina(Number($event.target.value))"
                >
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                  <option :value="200">200</option>
                </select>
              </div>
            </div>

            <div class="btn-group btn-group-sm">
              <button class="btn btn-outline-secondary btn-sm" @click="mudarPagina(paginacao.CurrentPage - 1)" :disabled="paginacao.CurrentPage <= 1">
                <i class="bi bi-chevron-left"></i>
                <span class="d-none d-sm-inline"> Anterior</span>
              </button>
              <button class="btn btn-outline-secondary btn-sm ms-2" @click="mudarPagina(paginacao.CurrentPage + 1)" :disabled="paginacao.CurrentPage >= paginacao.TotalPages">
                <span class="d-none d-sm-inline">Próxima </span>
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- COLUNA PRINCIPAL: PRODUTOS ALOCADOS NO EMBARQUE -->
    <!-- ============================================================ -->
    <div class="produtos-main" :class="{ 'produtos-main-expanded': !mostrarPainelDisponiveis }">
      <div class="produtos-main-toolbar">
        <h5 class="mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-list-check"></i>
          Produtos Alocados no Embarque
          <span v-if="itensAlocadosEnriquecidos.length > 0"
            class="badge bg-secondary bg-opacity-10 text-body-emphasis border border-secondary-subtle ms-1">
            {{ itensAlocadosEnriquecidos.length }} {{ itensAlocadosEnriquecidos.length === 1 ? 'produto' : 'produtos' }}
          </span>
        </h5>
        <button v-if="temSaldoPendente && podeEditar" type="button" class="btn btn-sm btn-success text-white fw-bold"
          @click="togglePainelDisponiveis"
          :title="mostrarPainelDisponiveis ? 'Fechar produtos disponíveis' : 'Adicionar Produtos ao Embarque'">
          <i class="bi" :class="mostrarPainelDisponiveis ? 'bi-x-lg' : 'bi-plus-lg'"></i>
          {{ mostrarPainelDisponiveis ? 'Fechar' : 'Adicionar Produtos' }}
        </button>
      </div>

      <div class="produtos-main-body" :class="{
        'dropzone-ativa': mostrarPainelDisponiveis,
        'drag-over': draggingOverId === '__alocados__'
      }"
        @dragenter="mostrarPainelDisponiveis && onDragEnter($event)"
        @dragover.prevent="mostrarPainelDisponiveis && onDragOver($event, '__alocados__')"
        @dragleave="mostrarPainelDisponiveis && onDragLeave($event)"
        @drop.prevent="mostrarPainelDisponiveis && onDrop($event)">
        <template v-if="itensAlocadosEnriquecidos.length === 0">
          <div v-if="mostrarPainelDisponiveis" class="empty-state">
            <i class="bi bi-download fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Arraste produtos do painel ao lado</p>
          </div>
          <div v-else class="empty-state">
            <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum produto alocado neste embarque.</p>
            <button v-if="temSaldoPendente && podeEditar" type="button"
              class="btn btn-sm btn-success text-white fw-bold mt-3" @click="togglePainelDisponiveis">
              <i class="bi bi-plus-lg me-1"></i> Adicionar Produtos ao Embarque
            </button>
          </div>
        </template>

        <template v-if="itensAlocadosEnriquecidos.length > 0">
          <div class="produtos-alocados-grid">
            <div v-for="itemEmb in itensAlocadosEnriquecidos" :key="itemEmb.idEmbarqueItem"
              class="produto-alocado-card">
              <div class="produto-alocado-header">
                <div class="card-sku" v-if="itemEmb.sku">{{ itemEmb.sku }}</div>
                <div class="produto-alocado-actions" v-if="podeEditar">
                  <span
                    :title="itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem)
                      ? 'Item já associado ao Packing List - edite-o no Packing List primeiro'
                      : 'Editar alocação'"
                  >
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-success border-0"
                      :class="{ 'disabled': itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem) }"
                      @click="abrirEdicao(itemEmb)"
                      :disabled="itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem)"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                  </span>
                  <span
                    :title="itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem)
                      ? 'Item já associado ao Packing List - remova-o do Packing List primeiro'
                      : 'Remover do embarque'"
                  >
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger border-0"
                      :class="{ 'disabled': itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem) }"
                      @click="excluirItem(itemEmb.idEmbarqueItem)"
                      :disabled="itemEstaAssociadoAPackingList(itemEmb.idEmbarqueItem)"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </span>
                </div>
              </div>
              <div class="card-title" style="margin-bottom: 0;">{{ itemEmb.nomeProduto || 'Produto #' +
                itemEmb.idProduto }}</div>
              <div class="card-filial" v-if="itemEmb.nomeFilial">
                <i class="bi bi-geo-alt me-1"></i>
                <span>{{ 'Filial ' + itemEmb.idFilial + ' - ' + itemEmb.nomeFilial }}</span>
                <!-- <span class="filial-id-badge">#{{ itemEmb.idFilial }}</span> -->
              </div>
              <div class="card-info-grid">
                <div class="card-info-row">
                  <div class="card-info-item" v-if="itemEmb.nomeFornecedor">
                    <span class="info-label-tag">Fornecedor</span>
                    <span class="info-value-tag">{{ itemEmb.nomeFornecedor }}</span>
                  </div>
                  <div class="card-info-item" v-if="itemEmb.numeroDocumentoEntrada">
                    <span class="info-label-tag">Doc. Entrada</span>
                    <span class="info-value-tag">{{ itemEmb.numeroDocumentoEntrada }}</span>
                  </div>
                </div>
                <div class="card-info-row">
                  <div class="card-info-item" v-if="itemEmb.numeroContratoFornecedor">
                    <span class="info-label-tag">Contrato</span>
                    <span class="info-value-tag">{{ itemEmb.numeroContratoFornecedor }}</span>
                  </div>
                  <div class="card-info-item" v-if="itemEmb.nomeReferenciaCota">
                    <span class="info-label-tag">Cota</span>
                    <span class="info-value-tag">{{ itemEmb.nomeReferenciaCota }}</span>
                  </div>
                  <div class="card-info-item" v-if="itemEmb.numeroLoteFornecedor">
                    <span class="info-label-tag">Lote Fab.</span>
                    <span class="info-value-tag">{{ itemEmb.numeroLoteFornecedor }}</span>
                  </div>
                </div>
              </div>
              <div class="card-saldo-separator"></div>
              <div class="card-saldo">
                <div class="saldo-row">
                  <span class="saldo-label">Qtd (Forn):</span>
                  <span class="saldo-value">{{ formatarQtd(itemEmb.quantidadeEmbarcadaUnFornecedor) }} {{
                    getTextoUnidade(itemEmb.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="saldo-row">
                  <span class="saldo-label">Qtd (Base):</span>
                  <span class="saldo-value">{{ formatarQtd(itemEmb.quantidadeEmbarcadaUnBasica) }} {{
                    getTextoUnidade(itemEmb.lstUnidadeMedidaBasica) }}</span>
                </div>
                <div class="card-saldo-separator"></div>
                <div class="saldo-row">
                  <span class="saldo-label">Peso Bruto (Documental):</span>
                  <span class="saldo-value">{{ formatarDecimal(itemEmb.pesoBrutoDocumentalKg) }} kg</span>
                </div>
                <div class="saldo-row">
                  <span class="saldo-label">Peso Líquido (Documental):</span>
                  <span class="saldo-value">{{ formatarDecimal(itemEmb.pesoLiquidoDocumentalKg) }} kg</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL DE ALOCAÇÃO (APENAS QUANTIDADES) -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <div v-if="showFractionModal" ref="modalAlocacaoRef" class="modal-overlay" :style="{ zIndex: zIndexEfetivo }" @click.self="fecharModal">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi" :class="editingItemId ? 'bi-pencil' : 'bi-arrows-split'"></i>
              <span class="me-1">{{ editingItemId ? 'Editar' : 'Alocar' }}</span>
              Produto no Embarque
            </h5>
            <button type="button" class="btn-close" @click="fecharModal"></button>
          </div>

          <div class="modal-body">
            <!-- Info de Origem -->
            <div class="modal-info-box">
              <div class="modal-sku" v-if="fractionModalData.sku">{{ fractionModalData.sku }}</div>
              <div class="modal-produto-nome">{{ fractionModalData.nomeProduto }}</div>

              <!-- <div class="info-item">
                <span class="info-label">NCM:</span>
                <span class="info-valor"><code>{{ fractionModalData.codigoNcm || '-' }}</code></span>
              </div> -->
              <!-- <div class="modal-info-grid"> -->
                <div class="modal-info-row">
                  <div class="modal-info-item">
                    <span class="info-label-tag">NCM:</span>
                    <span class="info-value-tag">{{ fractionModalData.codigoNcm || '-' }}</span>
                  </div>
                </div>
              <!-- </div> -->

              <div class="modal-info-grid">
                <div class="modal-info-row">
                  <div class="modal-info-item">
                    <span class="info-label-tag">Fornecedor</span>
                    <span class="info-value-tag">{{ fractionModalData.nomeFornecedor || '-' }}</span>
                  </div>
                  <div class="modal-info-item">
                    <span class="info-label-tag">Doc. Entrada</span>
                    <span class="info-value-tag">{{ fractionModalData.numeroDocumentoEntrada || '-' }}</span>
                  </div>
                </div>
                <div class="modal-info-row">
                  <div class="modal-info-item">
                    <span class="info-label-tag">Contrato</span>
                    <span class="info-value-tag">{{ fractionModalData.numeroContratoFornecedor || '-' }}</span>
                  </div>
                  <div class="modal-info-item">
                    <span class="info-label-tag">Cota</span>
                    <span class="info-value-tag">{{ fractionModalData.nomeReferenciaCota || '-' }}</span>
                  </div>
                  <div class="modal-info-item" v-if="fractionModalData.numeroLoteFornecedor">
                    <span class="info-label-tag">Lote Fab.</span>
                    <span class="info-value-tag">{{ fractionModalData.numeroLoteFornecedor }}</span>
                  </div>
                </div>
              </div>
              <div class="modal-saldo-separator"></div>
              <div class="info-item">
                <span class="info-label">Qtd (Forn):</span>
                <span class="info-valor">{{ formatarQtd(fractionModalData.saldoDisponivelForn) }} {{
                  getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Qtd (Base):</span>
                <span class="info-valor">{{ formatarQtd(fractionModalData.saldoDisponivelBase) }} {{
                  getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }}</span>
              </div>
            </div>

            <!-- Sliders de Quantidade -->
            <div class="sliders-stacked">
              <!-- Qtd Fornecedor -->
              <div class="slider-section">
                <div class="slider-section-header">
                  <i class="bi bi-box-seam me-1"></i>
                  Qtd. Fornecedor
                  <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor)
                    }})</small>
                </div>
                <div class="slider-row">
                  <input type="range" class="form-range slider-row-main" :min="0"
                    :max="fractionModalData.saldoDisponivelForn" step="0.1"
                    v-model.number="fractionModalData.quantidadeForn" />
                  <div class="slider-row-controls">
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1 unidade" @click="ajustarQuantidade('quantidadeForn', -1)"
                        :disabled="fractionModalData.quantidadeForn < 1">
                        <i class="bi bi-dash-lg"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0.1" @click="ajustarQuantidade('quantidadeForn', -0.1)"
                        :disabled="fractionModalData.quantidadeForn < 0.1">
                        <i class="bi bi-dash-lg"></i> 0,1
                      </button>
                    </div>
                    <div class="slider-row-value">
                      <input type="number" class="form-control form-control-sm" :min="0"
                        :max="fractionModalData.saldoDisponivelForn" step="0.01"
                        v-model.number="fractionModalData.quantidadeForn"
                        @blur="calcularConversao('fornecedor')" />
                      <span class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelForn) }}</span>
                    </div>
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1 unidade" @click="ajustarQuantidade('quantidadeForn', 1)"
                        :disabled="fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn">
                        <i class="bi bi-plus-lg"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1" @click="ajustarQuantidade('quantidadeForn', 0.1)"
                        :disabled="fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn">
                        <i class="bi bi-plus-lg"></i> 0,1
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Botão Calcular (conversão centralizada no backend) -->
              <div class="d-flex justify-content-end mb-2">
                <button type="button" class="btn btn-sm btn-outline-primary"
                  @click="calcularConversao('fornecedor')">
                  <i class="bi bi-calculator me-1"></i> Calcular
                </button>
              </div>

              <!-- Qtd Básica -->

              <div class="slider-section">
                <div class="slider-section-header">
                  <i class="bi bi-box me-1"></i>
                  Qtd. Básica
                  <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }})</small>
                </div>
                <div class="slider-row">
                  <input type="range" class="form-range slider-row-main" :min="0"
                    :max="fractionModalData.saldoDisponivelBase" step="0.1"
                    v-model.number="fractionModalData.quantidadeBase" />
                  <div class="slider-row-controls">
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1 unidade" @click="ajustarQuantidade('quantidadeBase', -1)"
                        :disabled="fractionModalData.quantidadeBase < 1">
                        <i class="bi bi-dash-lg"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0.1" @click="ajustarQuantidade('quantidadeBase', -0.1)"
                        :disabled="fractionModalData.quantidadeBase < 0.1">
                        <i class="bi bi-dash-lg"></i> 0,1
                      </button>
                    </div>
                    <div class="slider-row-value">
                      <input type="number" class="form-control form-control-sm" :min="0"
                        :max="fractionModalData.saldoDisponivelBase" step="0.01"
                        v-model.number="fractionModalData.quantidadeBase"
                        @blur="calcularConversao('base')" />
                      <span class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelBase) }}</span>
                    </div>
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1 unidade" @click="ajustarQuantidade('quantidadeBase', 1)"
                        :disabled="fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase">
                        <i class="bi bi-plus-lg"></i>
                      </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1" @click="ajustarQuantidade('quantidadeBase', 0.1)"
                        :disabled="fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase">
                        <i class="bi bi-plus-lg"></i> 0,1
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <hr class="slider-divider" />

              <div class="slider-section">
                <div class="slider-section-header">
                  <i class="bi bi-speedometer2 me-1"></i>
                  Pesos Documentais (KG)
                </div>
                <div class="d-flex gap-2">
                  <CampoInput id="pesoBrutoDocumentalKg" label="Peso Bruto" type="decimal" :casas-decimais="2" v-model="fractionModalData.pesoBrutoDocumentalKg" class="flex-fill" />
                  <CampoInput id="pesoLiquidoDocumentalKg" label="Peso Líquido" type="decimal" :casas-decimais="2" v-model="fractionModalData.pesoLiquidoDocumentalKg" class="flex-fill" />
                </div>
              </div>
            </div>

          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
              Cancelar
            </button>
            <button type="button" class="btn btn-success text-white fw-bold" @click="confirmarAlocacao" autofocus>
              <i class="bi bi-check-circle me-1"></i>
              {{ editingItemId ? 'Salvar Alterações' : 'Confirmar Alocação' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.produtos-smart-mode {
  position: relative;
  display: flex;
  gap: 0;
  height: 800px;
  overflow: hidden;
}

.disponiveis-panel {
  width: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s ease;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  display: flex;
  flex-direction: column;
}

.disponiveis-panel.disponiveis-visible {
  width: 28%;
  margin-right: 16px;
  height: 100%;
}

.disponiveis-panel-inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.disponiveis-header {
  padding: 12px 16px;
  background: var(--bs-warning-bg-subtle);
  border-bottom: 1px solid var(--bs-warning-border-subtle);
  flex-shrink: 0;
}

.disponiveis-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.disponiveis-header h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.disponiveis-header small {
  font-size: 0.7rem;
}

/* ============================================================ */
/* FILTROS DO PAINEL DE PRODUTOS DISPONÍVEIS */
/* ============================================================ */
.filtros-disponiveis {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filtro-group {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.filtro-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
  display: flex;
  align-items: center;
}

.filtro-group .form-control-sm {
  font-size: 0.75rem;
  padding: 3px 6px;
  border: none;
  background: transparent;
  flex: 1;
  min-width: 60px;
}

.filtro-group .form-control-sm:focus {
  box-shadow: none;
}

.tags-input-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 2px 4px;
  align-items: center;
}

.tag-badge {
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 0 4px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  font-weight: 600;
}

.tag-badge i {
  cursor: pointer;
}

.tag-badge i:hover {
  color: var(--bs-danger);
}

.disponiveis-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  position: relative;
}

.local-loading-overlay {
  position: absolute;
  inset: 0;
  background-color: var(--bs-body-bg);
  opacity: 0.7;
  z-index: 10;
}

.local-loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 11;
}

.produtos-main {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.produtos-main-expanded {
  width: 100%;
}

.produtos-main-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bs-success-bg-subtle);
  border-bottom: 1px solid var(--bs-success-border-subtle);
}

.produtos-main-toolbar h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.produtos-main-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  position: relative;
}

.produtos-main-body.dropzone-ativa {
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  margin: 8px;
  transition: all 0.3s ease;
  min-height: 200px;
}

.produtos-main-body.dropzone-ativa.drag-over {
  border-color: var(--bs-success);
  background-color: var(--bs-success-bg-subtle);
  transform: scale(1.01);
}

.produtos-alocados-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.produto-alocado-card {
  padding: 10px 12px;
  border: 1px solid var(--bs-border-color-translucent);
  border-left: 4px solid var(--bs-success);
  border-radius: 6px;
  background: var(--bs-body-bg);
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: var(--bs-secondary-color);
}

.empty-state p {
  font-size: 0.85rem;
}

.draggable-card {
  cursor: grab;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-warning);
  border-radius: 6px;
  background: var(--bs-body-bg);
  transition: transform 0.2s, box-shadow 0.2s;
}

.draggable-card:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.draggable-card:hover {
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
}

.card-saldo-esgotado {
  opacity: 0.5;
  border-left-color: var(--bs-danger);
  cursor: not-allowed;
}

.card-sku {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--bs-danger);
  margin-bottom: 2px;
  line-height: 1.2;
}

.card-title {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--bs-body-color);
  margin-bottom: 6px;
}

.card-filial {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.card-filial i {
  font-size: 0.65rem;
}

.filial-id-badge {
  font-size: 0.6rem;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 3px;
  padding: 0 4px;
  color: var(--bs-secondary-color);
}

.card-info-grid {
  border-top: 1px solid var(--bs-border-color-translucent);
  padding-top: 6px;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-info-row {
  display: flex;
  gap: 8px;
}

.card-info-item {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 2px;
  font-size: 0.68rem;
  align-items: baseline;
}

.info-label-tag {
  color: var(--bs-secondary-color);
  white-space: nowrap;
  font-size: 0.65rem;
}

.info-value-tag {
  font-weight: 600;
  color: var(--bs-body-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-saldo-separator {
  border-top: 1px solid var(--bs-border-color-translucent);
  margin: 4px 0;
}

.card-saldo {
  font-size: 0.75rem;
}

.saldo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.saldo-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.saldo-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.card-preco {
  font-size: 0.72rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

.produto-alocado-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}

.produto-alocado-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  margin-left: auto;
}

/* Ícones dos botões de ação - mais vivos quando habilitados, esmaecidos quando desabilitados */
.produto-alocado-actions .btn-outline-success:not(:disabled) i,
.produto-alocado-actions .btn-outline-danger:not(:disabled) i {
  opacity: 1;
}

.produto-alocado-actions .btn-outline-success:disabled i,
.produto-alocado-actions .btn-outline-danger:disabled i {
  opacity: 0.55;
}

/* MODAL */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.modal-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 560px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--bs-warning);
  color: #fff;
  border-bottom: 1px solid var(--bs-border-color);
}

.modal-header .btn-close {
  filter: invert(1);
}

.modal-title {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
  color: #fff;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
  background: var(--bs-tertiary-bg);
}

.modal-info-box {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.modal-sku {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--bs-danger);
  margin-bottom: 2px;
  line-height: 1.2;
}

.modal-produto-nome {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--bs-body-color);
  margin-bottom: 6px;
}

.modal-info-grid {
  border-top: 1px solid var(--bs-border-color-translucent);
  padding-top: 6px;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.modal-info-row {
  display: flex;
  gap: 8px;
}

.modal-info-item {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 2px;
  font-size: 0.68rem;
  align-items: baseline;
}

.modal-saldo-separator {
  border-top: 1px solid var(--bs-border-color-translucent);
  margin: 4px 0;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
  padding: 2px 0;
}

.info-item .info-label {
  font-weight: 600;
  min-width: 90px;
  color: var(--bs-secondary-color);
}

.info-item .info-valor {
  color: var(--bs-body-color);
}

/* SLIDERS */
.sliders-stacked {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slider-section {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

.slider-section-header {
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.slider-section-header small {
  font-weight: 400;
  font-size: 0.7rem;
}

.slider-divider {
  margin: 0;
  border: 0;
  border-top: 2px dashed var(--bs-border-color);
  opacity: 0.5;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-row-main {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.slider-row-controls {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.slider-row-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-row-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 110px;
}

.slider-row-value .form-control-sm {
  width: 100%;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
  text-align: center;
}

.slider-row-value .slider-max {
  font-size: 0.65rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
}

.slider-btn {
  font-size: 0.6rem;
  padding: 2px 8px;
  line-height: 1.3;
  border-width: 1px;
  white-space: nowrap;
  min-width: 36px;
}

.slider-btn-decimal {
  font-size: 0.55rem;
  opacity: 0.8;
  min-width: 32px;
}

/* PAGINAÇÃO DOS PRODUTOS DISPONÍVEIS */
.paginacao-disponiveis {
  padding: 8px 12px;
  border-top: 1px solid var(--bs-border-color);
  background: var(--bs-tertiary-bg);
  flex-shrink: 0;
}

.paginacao-disponiveis .form-select-sm {
  font-size: 0.75rem;
  padding: 2px 24px 2px 8px;
}

.paginacao-disponiveis .btn-sm {
  font-size: 0.75rem;
}

.paginacao-disponiveis .text-muted.small {
  font-size: 0.72rem;
}

@media (max-width: 1400px) {
  .produtos-alocados-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .produtos-alocados-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .produtos-smart-mode {
    flex-direction: column;
  }

  .disponiveis-panel.disponiveis-visible {
    width: 100%;
    margin-right: 0;
    margin-bottom: 16px;
  }

  .produtos-alocados-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .produtos-alocados-grid {
    grid-template-columns: 1fr;
  }
}
</style>
